import {
  monitoramentoDeSaldosSchema,
  type DefinirMonitoramentoDaConta,
  type MonitoramentoDeSaldos,
  type SalvarMinimosDeSaldo,
  conexaoGoogleSchema,
  conexaoMetaSchema,
  chaveDeIaSchema,
  credencialGoogleSchema,
  credencialMetaSchema,
  iniciarOauthGoogleSchema,
  listaDeContasDeAnuncioSchema,
  type ConexaoGoogle,
  type ConexaoMeta,
  type ConcluirOauthGoogle,
  type ChaveDeIa,
  type ContaDeAnuncio,
  type CredencialGoogle,
  type CredencialMeta,
  type IniciarOauthGoogle,
  type SalvarConexaoGoogle,
  type SalvarConexaoMeta,
  type SalvarCredencialGoogle,
  conexaoDoKommoSchema,
  type ConectarKommo,
  type ConexaoDoKommo,
  type MapeamentoDoFunil,
  type RecorteDoProjeto,
} from "@base/contracts";
import { z } from "zod";
import type { ClienteHttp } from "../../../shared/http/cliente-http";

const caminhoDoMeta = (clienteId: string) => `/v1/clientes/${encodeURIComponent(clienteId)}/conexoes/meta`;
const caminhoDoGoogle = (clienteId: string) => `/v1/clientes/${encodeURIComponent(clienteId)}/conexoes/google`;
const caminhoDoKommo = (clienteId: string) => `/v1/clientes/${encodeURIComponent(clienteId)}/conexoes/kommo`;

/** Porta de saida das Conexoes no front. */
export interface ConexoesGateway {
  kommo(clienteId: string): Promise<ConexaoDoKommo>;
  conectarKommo(clienteId: string, dados: ConectarKommo): Promise<ConexaoDoKommo>;
  mapearKommo(clienteId: string, mapeamento: MapeamentoDoFunil): Promise<ConexaoDoKommo>;
  sincronizarKommo(clienteId: string): Promise<ConexaoDoKommo>;
  definirCamposDoFiltro(clienteId: string, campos: readonly number[]): Promise<ConexaoDoKommo>;
  definirRecorte(clienteId: string, recorte: RecorteDoProjeto | null): Promise<ConexaoDoKommo>;
  credencialMeta(): Promise<CredencialMeta>;
  salvarCredencialMeta(token: string): Promise<CredencialMeta>;
  contasDeAnuncio(clienteId: string): Promise<readonly ContaDeAnuncio[]>;
  conexaoMeta(clienteId: string): Promise<ConexaoMeta>;
  salvarConexaoMeta(clienteId: string, dados: SalvarConexaoMeta): Promise<ConexaoMeta>;
  sincronizarMeta(clienteId: string): Promise<ConexaoMeta>;
  desconectarMeta(clienteId: string): Promise<void>;
  credencialGoogle(): Promise<CredencialGoogle>;
  salvarCredencialGoogle(dados: SalvarCredencialGoogle): Promise<CredencialGoogle>;
  iniciarOauthGoogle(): Promise<IniciarOauthGoogle>;
  concluirOauthGoogle(dados: ConcluirOauthGoogle): Promise<CredencialGoogle>;
  removerCredencialGoogle(): Promise<void>;
  contasGoogle(clienteId: string): Promise<readonly ContaDeAnuncio[]>;
  conexaoGoogle(clienteId: string): Promise<ConexaoGoogle>;
  salvarConexaoGoogle(clienteId: string, dados: SalvarConexaoGoogle): Promise<ConexaoGoogle>;
  desconectarGoogle(clienteId: string): Promise<void>;
  desconectarKommo(clienteId: string): Promise<void>;
  monitoramentoDeSaldos(): Promise<MonitoramentoDeSaldos>;
  lerSaldosAgora(): Promise<MonitoramentoDeSaldos>;
  salvarMinimosDeSaldo(dados: SalvarMinimosDeSaldo): Promise<MonitoramentoDeSaldos>;
  definirMonitoramentoDaConta(dados: DefinirMonitoramentoDaConta): Promise<MonitoramentoDeSaldos>;
  chaveDeIa(): Promise<ChaveDeIa>;
  salvarChaveDeIa(chave: string): Promise<ChaveDeIa>;
  removerChaveDeIa(): Promise<void>;
}

const CAMINHO_DO_MONITORAMENTO = "/v1/midia/monitoramento-de-saldos";

export class ConexoesGatewayHttp implements ConexoesGateway {
  constructor(private readonly http: ClienteHttp) {}

  kommo(clienteId: string): Promise<ConexaoDoKommo> {
    return this.http.requisitar(caminhoDoKommo(clienteId), conexaoDoKommoSchema);
  }

  conectarKommo(clienteId: string, dados: ConectarKommo): Promise<ConexaoDoKommo> {
    return this.http.requisitar(caminhoDoKommo(clienteId), conexaoDoKommoSchema, { metodo: "POST", corpo: dados });
  }

  mapearKommo(clienteId: string, mapeamento: MapeamentoDoFunil): Promise<ConexaoDoKommo> {
    return this.http.requisitar(`${caminhoDoKommo(clienteId)}/mapeamento`, conexaoDoKommoSchema, {
      metodo: "PUT",
      corpo: mapeamento,
    });
  }

  sincronizarKommo(clienteId: string): Promise<ConexaoDoKommo> {
    return this.http.requisitar(`${caminhoDoKommo(clienteId)}/sincronizacoes`, conexaoDoKommoSchema, {
      metodo: "POST",
    });
  }

  definirCamposDoFiltro(clienteId: string, campos: readonly number[]): Promise<ConexaoDoKommo> {
    return this.http.requisitar(`${caminhoDoKommo(clienteId)}/campos-do-filtro`, conexaoDoKommoSchema, {
      metodo: "PUT",
      corpo: { campos },
    });
  }

  definirRecorte(clienteId: string, recorte: RecorteDoProjeto | null): Promise<ConexaoDoKommo> {
    return this.http.requisitar(`${caminhoDoKommo(clienteId)}/recorte`, conexaoDoKommoSchema, {
      metodo: "PUT",
      corpo: { recorte },
    });
  }

  credencialMeta(): Promise<CredencialMeta> {
    return this.http.requisitar("/v1/midia/meta/credencial", credencialMetaSchema);
  }

  salvarCredencialMeta(token: string): Promise<CredencialMeta> {
    return this.http.requisitar("/v1/midia/meta/credencial", credencialMetaSchema, { metodo: "PUT", corpo: { token } });
  }

  async contasDeAnuncio(clienteId: string): Promise<readonly ContaDeAnuncio[]> {
    return (await this.http.requisitar(`${caminhoDoMeta(clienteId)}/contas`, listaDeContasDeAnuncioSchema)).dados;
  }

  conexaoMeta(clienteId: string): Promise<ConexaoMeta> {
    return this.http.requisitar(caminhoDoMeta(clienteId), conexaoMetaSchema);
  }

  salvarConexaoMeta(clienteId: string, dados: SalvarConexaoMeta): Promise<ConexaoMeta> {
    return this.http.requisitar(caminhoDoMeta(clienteId), conexaoMetaSchema, { metodo: "PUT", corpo: dados });
  }

  sincronizarMeta(clienteId: string): Promise<ConexaoMeta> {
    return this.http.requisitar(`${caminhoDoMeta(clienteId)}/sincronizacoes`, conexaoMetaSchema, { metodo: "POST" });
  }

  async desconectarMeta(clienteId: string): Promise<void> {
    await this.http.requisitar(caminhoDoMeta(clienteId), z.null(), { metodo: "DELETE" });
  }

  credencialGoogle(): Promise<CredencialGoogle> {
    return this.http.requisitar("/v1/midia/google/credencial", credencialGoogleSchema);
  }

  salvarCredencialGoogle(dados: SalvarCredencialGoogle): Promise<CredencialGoogle> {
    return this.http.requisitar("/v1/midia/google/credencial", credencialGoogleSchema, {
      metodo: "PUT",
      corpo: dados,
    });
  }

  iniciarOauthGoogle(): Promise<IniciarOauthGoogle> {
    return this.http.requisitar("/v1/midia/google/oauth", iniciarOauthGoogleSchema);
  }

  concluirOauthGoogle(dados: ConcluirOauthGoogle): Promise<CredencialGoogle> {
    return this.http.requisitar("/v1/midia/google/oauth", credencialGoogleSchema, {
      metodo: "POST",
      corpo: dados,
    });
  }

  async removerCredencialGoogle(): Promise<void> {
    await this.http.requisitar("/v1/midia/google/credencial", z.null(), { metodo: "DELETE" });
  }

  async contasGoogle(clienteId: string): Promise<readonly ContaDeAnuncio[]> {
    return (await this.http.requisitar(`${caminhoDoGoogle(clienteId)}/contas`, listaDeContasDeAnuncioSchema)).dados;
  }

  conexaoGoogle(clienteId: string): Promise<ConexaoGoogle> {
    return this.http.requisitar(caminhoDoGoogle(clienteId), conexaoGoogleSchema);
  }

  salvarConexaoGoogle(clienteId: string, dados: SalvarConexaoGoogle): Promise<ConexaoGoogle> {
    return this.http.requisitar(caminhoDoGoogle(clienteId), conexaoGoogleSchema, { metodo: "PUT", corpo: dados });
  }

  async desconectarGoogle(clienteId: string): Promise<void> {
    await this.http.requisitar(caminhoDoGoogle(clienteId), z.null(), { metodo: "DELETE" });
  }

  async desconectarKommo(clienteId: string): Promise<void> {
    await this.http.requisitar(caminhoDoKommo(clienteId), z.null(), { metodo: "DELETE" });
  }

  monitoramentoDeSaldos(): Promise<MonitoramentoDeSaldos> {
    return this.http.requisitar(CAMINHO_DO_MONITORAMENTO, monitoramentoDeSaldosSchema);
  }

  lerSaldosAgora(): Promise<MonitoramentoDeSaldos> {
    return this.http.requisitar(`${CAMINHO_DO_MONITORAMENTO}/leituras`, monitoramentoDeSaldosSchema, { metodo: "POST" });
  }

  salvarMinimosDeSaldo(dados: SalvarMinimosDeSaldo): Promise<MonitoramentoDeSaldos> {
    return this.http.requisitar(`${CAMINHO_DO_MONITORAMENTO}/minimos`, monitoramentoDeSaldosSchema, {
      metodo: "PUT",
      corpo: dados,
    });
  }

  definirMonitoramentoDaConta(dados: DefinirMonitoramentoDaConta): Promise<MonitoramentoDeSaldos> {
    return this.http.requisitar(`${CAMINHO_DO_MONITORAMENTO}/contas`, monitoramentoDeSaldosSchema, {
      metodo: "PUT",
      corpo: dados,
    });
  }

  chaveDeIa(): Promise<ChaveDeIa> {
    return this.http.requisitar("/v1/midia/openai/credencial", chaveDeIaSchema);
  }

  salvarChaveDeIa(chave: string): Promise<ChaveDeIa> {
    return this.http.requisitar("/v1/midia/openai/credencial", chaveDeIaSchema, {
      metodo: "PUT",
      corpo: { chave },
    });
  }

  async removerChaveDeIa(): Promise<void> {
    await this.http.requisitar("/v1/midia/openai/credencial", z.null(), { metodo: "DELETE" });
  }
}
